import React from "react";
import Swal from "sweetalert2";
import { loadFilter, saveFilter } from "../../helper/helperGeneral";


const ComTable = ({
    data,
    handleMoveDown,
    handleDelete,
    handleEdit,
    handleReinvite,
    handleVerify,
    handleDeactivate,
    handleReactivate,
    handleAccept,
    handleDecline,
    handleStop,
    handleMoveUp,
    isUndefinedIsLoading = false,
    headerLeft,
    headerRight,
    footerLeft,
    footerRight,
    handlePrevPage,
    handleNextPage,
    handleReload,
    showCopyToClipboard,
    showCompactMode,
}) => {
    const [setting, setSetting] = React.useState(loadFilter("ComTable", {
        isCompact: true,
    }));
    let renderType = "table"
    if (data === undefined && isUndefinedIsLoading === true) renderType = "loading";
    else if (!data || data.length === 0) renderType = "empty";
    const shouldRenderCompact = (showCompactMode === true && setting?.isCompact === true);

    const headers = data && data.length > 0
        ? Object.keys(data[0]).filter((key) => !key.startsWith("_") && key !== "id")
        : [] || [];

    const formatHeader = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

    const showExtra = (row) => {
        const div = document.createElement('div');
        div.textContent = JSON.stringify(row._metadata, null, 2);
        Swal.fire({
            title: "Additional Details",
            html: `<pre style="text-align: left; direction: ltr;">${div.innerHTML}</pre>`,
            width: '60%',
            confirmButtonText: "Close",
            showCloseButton: true,
            showCancelButton: false,
            showConfirmButton: true,
            customClass: {
                popup: 'swal-wide',
                confirmButton: 'btn btn-secondary',
            },
        });
    };

    React.useEffect(() => {
        saveFilter("ComTable", setting);
    }, [setting]);


    return <>
        <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex gap-2">{headerLeft}</div>
            <div className="d-flex gap-2">
                {headerRight}
                {handleReload &&
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleReload} title="Reload data">
                        <i className={`fa fa-sync-alt`} />
                    </button>}
                {(showCopyToClipboard === true) &&
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title="Copy to clipboard"
                        disabled={(data === undefined || data?.length === 0)}
                        onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                            Swal.fire({
                                icon: 'success',
                                title: 'Copied to clipboard',
                                text: 'Data has been copied to clipboard.',
                                showConfirmButton: false,
                                timer: 1000,
                            });
                        }}>
                        <i className={`fa fa-copy`} />
                    </button>}
                {(showCompactMode === true) &&
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setSetting(prev => ({ ...prev, isCompact: !prev.isCompact })) }}
                        title={setting.isCompact ? "Standard view" : "Compact view"}
                    >
                        <i className={`fa ${setting.isCompact ? "fa-align-justify" : "fa-bars"}`} />
                    </button>}
            </div>
        </div>
        <div className="container-fluid px-0 border border-1 rounded-2">
            {(renderType === "loading") &&
                <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            }
            {(renderType === "empty") &&
                <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
                    <div className="text-secondary fs-7">
                        No data available
                    </div>
                </div>}
            {(renderType === "table") &&
                <table
                    className={`table ${shouldRenderCompact ? "table-sm" : ""} w-100 table-striped table-hover m-0`}
                    style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead className="thead-light">
                        <tr>
                            {headers.map((key) => (
                                <th key={key} className={`bg-light text-dark text-nowrap ${shouldRenderCompact ? "fs-9 py-1" : "fs-7 py-2"}`}>
                                    {formatHeader(key)}
                                </th>
                            ))}
                            <th className={`bg-light text-dark text-nowrap ${shouldRenderCompact ? "py-1" : "py-2"}`} style={{ width: "100px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map((row, idx) => {
                            return <tr key={idx} style={{ borderBottom: "1px solid #dee2e6" }}>
                                {headers.map((key) => (
                                    <td
                                        key={key}
                                        className={`align-top ${shouldRenderCompact ? "fs-9 py-1" : "fs-7 py-2"} ${(['message', 'note', 'description'].includes(key?.toLocaleLowerCase()) ? "text-break" : "text-nowrap")}`}
                                    >
                                        {row[key]}
                                        {(row._hasCopyClipboard === key) && <i
                                            className="fa fa-copy text-secondary ms-3"
                                            role="button"
                                            onClick={() => navigator.clipboard.writeText(row[key])}
                                            title="Copy to clipboard"
                                        />}
                                        {(row._hasBadge?.key === key) &&
                                            <span className={"ms-2 " + row._hasBadge.style}>{row._hasBadge.value}</span>}
                                    </td>
                                ))}
                                <td className="text-nowrap text-center">
                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                        {(handleMoveUp && row._hasMoveUp === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleMoveUp(row)} title="Move Up"><i className="fa fa-arrow-up" /></button>}
                                        {(handleMoveDown && row._hasMoveDown === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleMoveDown(row)} title="Move Down"><i className="fa fa-arrow-down" /></button>}
                                        {(handleEdit && row._hasEdit === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleEdit(row)} title="Edit">Edit</button>}
                                        {(handleReinvite && row._hasReinvite === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleReinvite(row)} title="Reinvite">Reinvite</button>}
                                        {(handleVerify && row._hasVerify === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleVerify(row)} title="Verify">Verify</button>}
                                        {(handleDeactivate && row._hasDeactivate === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleDeactivate(row)} title="Deactivate">Deactivate</button>}
                                        {(handleReactivate && row._hasReactivate === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleReactivate(row)} title="Reactivate">Reactivate</button>}
                                        {(handleAccept && row._hasAccept === true) && <button className="fs-8 btn btn-sm btn-outline-success" title="Accept" onClick={() => handleAccept(row)}><i className="fa fa-check" /> Accept</button>}
                                        {(handleDecline && row._hasDecline === true) && <button className="fs-8 btn btn-sm btn-outline-danger" title="Decline" onClick={() => handleDecline(row)}><i className="fa fa-times" /> Decline</button>}
                                        {(handleStop && row._hasStop === true) && <button className="fs-8 btn btn-sm btn-outline-secondary" role="button" onClick={() => handleStop(row)} title="Stop"><i className="fa fa-stop" /> Stop</button>}
                                        {(handleDelete && row._hasDelete === true) && <i className="fa fa-trash-alt text-danger" role="button" onClick={() => handleDelete(row)} title="Delete" />}
                                        {row._metadata && <i className="fa fa-info-circle text-secondary" role="button" onClick={() => showExtra(row)} title="Extra Data" />}
                                    </div>
                                </td>
                            </tr>
                        })}
                    </tbody>
                </table>}
        </div >
        <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex gap-2">{footerLeft}</div>
            <div className="d-flex gap-2">
                {footerRight}
                {handlePrevPage &&
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handlePrevPage} title="Previous Page" >
                        <i className={`fa fa-chevron-left`} />
                    </button>}
                {handleNextPage &&
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleNextPage} title="Next Page">
                        <i className={`fa fa-chevron-right`} />
                    </button>}
            </div>
        </div>
    </>
}

export default ComTable;